import { useEffect, useState } from 'react'

/**
 * Contact numbers/addresses of the record being edited, handed from the edit
 * form to the action widget so SMS/Voice/Email/WhatsApp target the entity's
 * real contact points (the legacy forms rendered one checkbox per number).
 */
export interface EntityContact {
  mobileNo: string
  mobileNo1: string
  mobileNo2: string
  email: string
  email1: string
  email2: string
}

export const EMPTY_CONTACT: EntityContact = {
  mobileNo: '',
  mobileNo1: '',
  mobileNo2: '',
  email: '',
  email1: '',
  email2: '',
}

export type TriValues = [string, string, string]
export type TriChecked = [boolean, boolean, boolean]

/** First non-empty slot checked by default, matching the legacy forms. */
export function defaultTriChecked(values: TriValues): TriChecked {
  const first = values.findIndex((v) => v.trim() !== '')
  return [first === 0, first === 1, first === 2]
}

/**
 * Checkbox selection over the three contact slots. `selected` keeps the slot
 * positions: unchecked or empty slots come back as '' so the payload's
 * mobileNo/mobileNo1/mobileNo2 (or email/email1/email2) line up with the
 * legacy BO fields.
 */
export function useTriSelection(values: TriValues) {
  const [checked, setChecked] = useState<TriChecked>(() => defaultTriChecked(values))

  // Re-derive the default when the underlying contact values change.
  useEffect(() => {
    setChecked(defaultTriChecked(values))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values[0], values[1], values[2]])

  const toggle = (index: 0 | 1 | 2, value: boolean) => {
    setChecked((prev) => {
      const next = [...prev] as TriChecked
      next[index] = value
      return next
    })
  }

  const selected = values.map((v, i) => (checked[i] && v.trim() !== '' ? v : '')) as TriValues
  const anySelected = selected.some((v) => v !== '')
  const reset = () => setChecked(defaultTriChecked(values))

  return { checked, toggle, selected, anySelected, reset }
}

interface ContactCheckboxesProps {
  /** Group label, e.g. "Mobile Number(s)". */
  label: string
  /** Unique prefix for the checkbox input ids (labels bind to them). */
  idPrefix: string
  values: TriValues
  checked: TriChecked
  onToggle: (index: 0 | 1 | 2, checked: boolean) => void
  /** Shown when every slot is empty. */
  emptyMessage: string
}

/** One checkbox per non-empty contact slot, as the legacy send forms rendered. */
export function ContactCheckboxes({
  label,
  idPrefix,
  values,
  checked,
  onToggle,
  emptyMessage,
}: ContactCheckboxesProps) {
  const hasAny = values.some((v) => v.trim() !== '')

  return (
    <div className="form-group">
      <span className="form-label">{label}</span>
      {!hasAny && <div className="form-hint">{emptyMessage}</div>}
      {values.map((value, index) =>
        value.trim() === '' ? null : (
          <label
            key={index}
            className="checkbox-label"
            htmlFor={`${idPrefix}-${index}`}
            style={{ display: 'flex', gap: 6, alignItems: 'center' }}
          >
            <input
              id={`${idPrefix}-${index}`}
              type="checkbox"
              checked={checked[index]}
              onChange={(e) => onToggle(index as 0 | 1 | 2, e.target.checked)}
            />
            <span>{value}</span>
          </label>
        ),
      )}
    </div>
  )
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * "2026-08-13T15:30" (datetime-local) or "2026-08-13" (date) →
 * "13-Aug-2026 15:30:00" — the format every legacy save sent its dates in.
 * '' stays ''.
 */
export function toLegacyDateTime(value: string): string {
  if (!value) return ''
  const [datePart, timePart = '00:00'] = value.split('T')
  const [y, m, d] = datePart.split('-').map(Number)
  if (!y || !m || m < 1 || m > 12 || !d) return value
  const [hh = '00', mm = '00'] = timePart.split(':')
  return `${String(d).padStart(2, '0')}-${MONTHS[m - 1]}-${y} ${hh.padStart(2, '0')}:${mm.padStart(2, '0')}:00`
}

/** The current date-time in `<input type="datetime-local">` value format. */
export function nowForDateTimeInput(): string {
  const now = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`
}
